import { Phone, MapPin, Instagram, MessageCircle, ArrowUpRight } from "lucide-react";
import { WHATSAPP_URL, TEL_URL, INSTAGRAM_URL, MAPS_URL, PHONE_DISPLAY } from "../i18n";

export const Contact = ({ t }) => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-12 max-w-2xl">
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-green-600">
            {t.contact.label}
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-neutral-950 sm:text-4xl lg:text-5xl">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-neutral-600">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-0 border border-neutral-200 lg:grid-cols-12">
          <div className="space-y-0 divide-y divide-neutral-200 lg:col-span-5 lg:border-r lg:border-neutral-200">
            <InfoRow
              testId="contact-phone"
              icon={<Phone size={18} />}
              label={t.contact.phone}
              value={PHONE_DISPLAY}
              href={TEL_URL}
            />
            <InfoRow
              testId="contact-location"
              icon={<MapPin size={18} />}
              label={t.contact.location}
              value={t.contact.address}
              href={MAPS_URL}
              external
            />
            <InfoRow
              testId="contact-instagram"
              icon={<Instagram size={18} />}
              label={t.contact.instagram}
              value="@cng_autocare_tz"
              href={INSTAGRAM_URL}
              external
            />
            <div className="p-7">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group inline-flex w-full items-center justify-center gap-3 bg-green-600 px-6 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-green-700 hover:shadow-lg sm:w-auto"
              >
                <MessageCircle size={18} />
                {t.hero.cta}
              </a>
            </div>
          </div>

          <div className="relative overflow-hidden bg-neutral-100 lg:col-span-7">
            <div className="relative h-[320px] w-full sm:h-[440px] lg:h-full lg:min-h-[440px]">
              <iframe
                title="CNG AutoCare on Google Maps"
                data-testid="map-iframe"
                src="https://www.google.com/maps?q=CNG+AutoCare+Ubungo+Maziwa+Road+Dar+es+Salaam&hl=en&z=16&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                className="absolute inset-0 h-full w-full grayscale-[0.08]"
              />
              <div className="pointer-events-none absolute bottom-5 left-5 right-5 flex items-center justify-between gap-4 bg-white/95 p-4 backdrop-blur-sm shadow-lg">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                    CNG AutoCare
                  </div>
                  <div className="mt-1 text-sm font-bold text-neutral-950">
                    {t.contact.address}
                  </div>
                </div>
                <a
                  href="https://share.google/hxQQUWQCAtieAuv41"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="map-link"
                  className="pointer-events-auto flex items-center gap-2 bg-neutral-950 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-green-600"
                >
                  {t.contact.directions}
                  <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const InfoRow = ({ testId, icon, label, value, href, external }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    data-testid={testId}
    className="group flex items-start gap-5 p-7 transition-colors duration-200 hover:bg-neutral-50"
  >
    <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-neutral-900 bg-white text-neutral-900 transition-colors duration-200 group-hover:border-green-600 group-hover:bg-green-600 group-hover:text-white">
      {icon}
    </div>
    <div className="flex-1">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
        {label}
      </div>
      <div className="mt-1 font-display text-base font-bold text-neutral-950">
        {value}
      </div>
    </div>
    <ArrowUpRight
      size={16}
      className="mt-2 shrink-0 text-neutral-300 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-green-600"
    />
  </a>
);
